import { useRouter } from "next/router";

import { BreadcrumbJsonLd, NextSeo, ProductJsonLd } from "next-seo";

import { useEffect } from "react";

import { ProductList } from "@/components/ProductList";
import { DEFAULT_LOCALE, SITE_ORIGIN } from "@/features/i18n/i18n.config.mjs";
import { eventAddToCart, eventProductDetail } from "@/lib/Analytics/Functions";
import type { VariantPage } from "@/lib/api/formatting/rorosTweed/productPage";
import getPriceList from "@/lib/centra/getPriceList";
import type { PriceAndSize } from "@/lib/centra/selection/selectionFormatter";
import useSelection from "@/lib/hooks/useSelection";
import type { IResolvedProductCard } from "@/types/product";

import { useTranslations } from "next-intl";
import ImageGrid from "./ImageGrid";
import Info from "./Info";
import PackshotGallery from "./PackshotGallery";
import ProductDesigner from "./ProductDesigner";
import styles from "./productPage.module.scss";
import SeriesCards from "./SeriesCards";

interface UIProps {
  product: VariantPage;
  relatedProducts?: IResolvedProductCard[];
  addPriceAndSize: PriceAndSize;
}
interface IProductJsonLd {
  productName: string;
  images?: string[];
  description?: string;
  brand?: string;
  color?: string;
  filterColor?: string;
  keywords?: string;
  sku?: string;
  mpn?: string;
  offers?: {
    price: number;
    priceCurrency: string;
    itemCondition: string;
    availability: string;
    url: string;
    seller: {
      name: string;
    };
  }[];
}
const buildProductJsonLd = (product: VariantPage, url: string) => {
  const keywords = [
    product?.seo?.keywords,
    product?.seo?.materialKeywords,
    product?.seo?.colorKeywords,
  ]
    .filter((value) => value)
    .join(", ");
  const sku = product?.info?.details?.sku || product?.sku;
  const jsonLd: IProductJsonLd = {
    productName: `${product.name} - ${product.variantName}`,
    images: product?.packShotGallery?.media
      ?.slice(0, 3)
      .map((image) => image.uri),
    description: product?.seo?.description || "",
    brand: product?.seo?.brand || "Røros Tweed",
    color: product?.variantName,
    filterColor: product?.color,
    keywords,
    sku,
    mpn: sku,
  };

  const validPrices = product?.info?.details?.prices?.filter(
    (price) => price.priceAsNumber !== 0
  );

  const offers = validPrices
    ?.map((price) => ({
      price: price?.priceAsNumber,
      priceCurrency: price?.currency || "",
      itemCondition: "https://schema.org/NewCondition",
      availability: product?.info?.details?.stock
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      url,
      seller: {
        name: "Røros Tweed",
      },
    }))
    .reverse();

  jsonLd.offers = offers;
  return jsonLd;
};

const buildBreadcrumbItems = (
  product: VariantPage,
  origin: string,
  localePrefix: string
) => {
  const items = [
    {
      position: 1,
      name: "Home",
      item: `${origin}${localePrefix || "/"}`,
    },
  ];

  product?.breadcrumbs?.forEach((crumb, index) => {
    if (!crumb?.categoryName || !crumb?.categoryUri) return;
    items.push({
      position: index + 2,
      name: crumb.categoryName,
      item: `${origin}${localePrefix}/category/${crumb.categoryUri}`,
    });
  });

  items.push({
    position: items.length + 1,
    name: `${product.name} - ${product.variantName}`,
    item: `${origin}${localePrefix}/product/${product.uri}`,
  });

  return items;
};
const UI = ({ product, relatedProducts, addPriceAndSize }: UIProps) => {
  const environmentGallery = product.environmentGallery;
  const { data, priceList } = useSelection();
  const info = product.info;
  const series = product.relatedSeries || [];
  const designer = product.designer;
  const { asPath, locale } = useRouter();
  const origin = SITE_ORIGIN;
  const localePrefix = locale && locale !== DEFAULT_LOCALE ? `/${locale}` : "";
  const url = `${origin}${localePrefix}${asPath.split("?")[0]}`;

  const type = "website";

  const t = useTranslations();

  const gtagAddToCart = () => {
    eventAddToCart({
      currency: data?.selection?.currency || "NOK",
      value:
        getPriceList(product.info?.details?.prices || [], +priceList)
          .priceAsNumber || 0,
      items: [
        {
          item_id: product.id || "",
          item_name: product.name || "",
          item_category: product.type || "",
          item_variant: product.variantName || "",
          price:
            getPriceList(product.info?.details?.prices || [], +priceList)
              .priceBeforeDiscountAsNumber || 0,
          quantity: 1,
        },
      ],
    });
  };

  useEffect(() => {
    eventProductDetail({
      currency: data?.selection?.currency || "NOK",
      items: [
        {
          item_id: product.id || "",
          item_name: product.name || "",
          item_category: product.type || "",
          item_variant: product.variantName || "",
          price:
            getPriceList(product.info?.details?.prices || [], +priceList)
              .priceBeforeDiscountAsNumber || 0,
          quantity: 1,
        },
      ],
    });
  }, []);

  return (
    <>
      <NextSeo
        title={`${product?.name} - ${product?.variantName}`}
        description={product?.seo?.metaDescription || product?.name}
        openGraph={{
          url,
          title: `${product?.name} - ${product?.variantName}`,
          type,
          images: product?.packShotGallery?.media?.map((image) => ({
            url: image.uri,
            alt: image.alt,
            width: 1200,
            height: 1500,
          })),
        }}
      />
      <ProductJsonLd {...buildProductJsonLd(product, url)} />
      <BreadcrumbJsonLd
        itemListElements={buildBreadcrumbItems(product, origin, localePrefix)}
      />

      <main className={styles.container}>
        <PackshotGallery {...product?.packShotGallery} />
        <Info
          {...info}
          addPriceAndSize={addPriceAndSize}
          gtagAddToCart={gtagAddToCart}
        />
        <SeriesCards series={series} />
        <ImageGrid {...environmentGallery} />
        <ProductDesigner designer={designer} />
        <div className={styles.relatedProducts}>
          {relatedProducts && relatedProducts.length > 0 && (
            <ProductList
              products={relatedProducts}
              title={t("Related products")}
            />
          )}
        </div>
      </main>
    </>
  );
};

export default UI;
